<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { $router } from '@/router'
import { $bus, mqtt } from '@/common/events'
import { defaultServiceOptions } from '@/common/config'
import { dialog } from '@/common/dialog'
import { toast } from '@/common/toast'
import { utc, Stopwatch } from '@/common/helper'
import { formatRelativeTime, formatLocaleDate, formatLocaleTime } from '@/common/format'
import { hcu } from '@/common/hcu'
import { useTitle } from '@/composables/title'
import { useTranslate } from '@/composables/language'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useVirtualKeyboard } from '@/composables/keyboard'
import { useAppState } from '@/composables/app'
import { useSession } from '@/composables/session'
import { useSettings } from '@/composables/settings'
import { useSystemStatus } from '@/composables/system'
import { useCabinetAlarm } from '@/composables/alarm'
import { canInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
import { useCabinetUnits } from "@/composables/cabinet.js"
import { useAuthState } from '@/composables/auth'
import { OrganizationService } from '@/services/OrganizationService.js'
import { ArticleService } from '@/services/ArticleService.js'
import { CabinetService } from '@/services/CabinetService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { CacheService } from '@/services/CacheService.js'
import { UserService } from '@/services/UserService.js'
import { LogService } from '@/services/LogService.js'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import Pagination from '@/components/Pagination.vue'
import Drawer from '@/components/Drawer.vue'
import Toast from '@/components/Toast.vue'
import Fullscreen from '@/components/Fullscreen.vue'
import WarningBanner from '@/components/WarningBanner.vue'
import AutocompleteTextBox from '@/components/AutocompleteTextBox.vue'
import ReferencePosition from '@/components/Maintenance/ReferencePosition.vue'
import TextArea from '@/components/TextArea.vue'

import { useAlarmState, onAlarmContinue } from './composables/AlarmState.js'
import { useAlertBanner } from './composables/AlertBanner.js'
import { useArticleSearch, onSearchResultSelect } from './composables/ArticleSearch.js'
import { useArticleStock } from './composables/ArticleStock.js'
import { useArticleWeight } from './composables/ArticleWeight.js'
import { useCatalogState, onArticleSelect, onCategorySelect, onParameterSelect } from './composables/CatalogState.js'
import { useCatalogHelper } from './composables/CatalogHelper.js'
import { useCatalogLoader, onCatalogLoad } from './composables/CatalogLoader.js'
import { useCabinetState } from './composables/CabinetState.js'
import { useUserInventory } from './composables/UserInventory.js'

import CategorySelect from './components/CategorySelect.vue'
import ParameterSelect from './components/ParameterSelect.vue'
import ArticleSelect from './components/ArticleSelect.vue'
import PrivateTraySelect from './components/PrivateTraySelect.vue'
import UserInventory from './components/UserInventory.vue'
import SearchResults from './components/SearchResults.vue'
import ArticleModal from './components/ArticleModal.vue'
import PrivateTrayModal from './components/PrivateTrayModal.vue'
import AlarmModal from './components/AlarmModal.vue'
import AlertBanner from './components/AlertBanner.vue'
import ErrorMessage from './components/ErrorMessage.vue'
import FilterOptionsDrawer from './components/FilterOptionsDrawer.vue'

const catalogStopwatch = new Stopwatch();

let openTimer = null;

//Initialize services
const serviceOptions = defaultServiceOptions();
const organizationService = new OrganizationService(serviceOptions);
const articleService = new ArticleService(serviceOptions);
const cabinetService = new CabinetService(serviceOptions);
const configService = new ConfigService(); //Local only
const cacheService = new CacheService(); //Local only
const userService = new UserService(serviceOptions);
const logService = new LogService(serviceOptions);

const { t, translateError } = useTranslate();
const { isKeyboardVisible } = useVirtualKeyboard();
const { latestStatus, isServiceMode } = useSystemStatus();
const { showUserItemsDisplayedOnce } = useSession();
const { alarmBus } = useCabinetAlarm();
const { settings } = useSettings();
const { appState } = useAppState();
const authStore = useAuthState();

//Load composables with initial state
const alarmState = useAlarmState({ clear: true });
const alertBanner = useAlertBanner({ clear: true });
const articleSearch = useArticleSearch({ clear: true });
const articleStock = useArticleStock({ clear: true });
const articleWeight = useArticleWeight({ clear: true });
const cabinetState = useCabinetState({ clear: true });
const catalogHelper = useCatalogHelper({ clear: true });
const catalogLoader = useCatalogLoader({ clear: true });
const catalogState = useCatalogState({ clear: true });
const userInventory = useUserInventory({ clear: true });

//Composable and service instances
const crossReferenceBundle = {
  organizationService,
  articleService,
  cabinetService,
  configService,
  cacheService,
  userService,
  logService,
  alarmState,
  alertBanner,
  articleSearch,
  articleStock,
  articleWeight,
  cabinetState,
  catalogHelper,
  catalogLoader,
  catalogState,
  userInventory,
  authStore, t,
};

//Cross-referencing between instances
useAlarmState(crossReferenceBundle);
useAlertBanner(crossReferenceBundle);
useArticleSearch(crossReferenceBundle);
useArticleStock(crossReferenceBundle);
useArticleWeight(crossReferenceBundle);
useCabinetState(crossReferenceBundle);
useCatalogHelper(crossReferenceBundle);
useCatalogLoader(crossReferenceBundle);
useCatalogState(crossReferenceBundle);
useUserInventory(crossReferenceBundle);

//Unpack variables and functions
const {
  isHaltAlarm,
  isCurtainAlarm,
  isDisruptAlarm,
  isAnyAlarm,
  clearAlarm,
} = alarmState;

const {
  warningBannerType,
  warningBannerMessage,
  warningBannerSeverity,
  warningBannerModal,
  isWarningBannerVisible,
  showWarningBanner,
  hideWarningBanner,
} = alertBanner;

const {
  searchText,
  searchResults,
  isSearchFilter,
  clearSearchText,
  buildSearchKeywords,
} = articleSearch;

const {
  quantity,
  quantityFactor,
  isStockMismatch,
  isEditStockQuantity,
  hasStockQuantityChanged,
  stockQuantityChange,
  initialStockQuantity,
  virtualStockQuantityNumeric,
  virtualStockQuantity,
  articleLowStockThreshold,
  virtualStockQuantityState,
  canApplyStock,
  stockComment,
  commentText,
  isCommentEdit,
  hasEditCommentConditions,
  canEditComment,
  canSaveStockQuantity,
  returnArticle,
  returnArticleTrayName,
  takeArticle,
  takeArticleTrayName,
  isReturnProcedure,
  isTakingProcedure,
  isChoosingAction,
  itemBorrowed,
  itemAvailable,
  availableSingleArticlesCount,
  canTransferStock,
  pendingTransferRecord,
  pendingTransferQuantity,
  isStockTransferApplied,
  isStockTransferRequest,
  rotateQuantityFactor,
  updateQuantity,
  stockSum,
  cancelComment,
  applyStockTransfer,
  undoStockTransfer,
} = articleStock;

const {
  isScaleReady,
  isScaleCalibration,
  isScaleBanner,
  isItemWeightCalibrated,
  scaleItemsUserCount,
  scaleItemsUserCountFormatted,
  scaleLastValue,
  scaleLastValueFormatted,
  scaleLastValueIntegerFormatted,
  scaleLastUnit,
  scaleInsertWeight,
  scaleInsertWeightFormatted,
  scaleItemWeight,
  scaleItemWeightFormatted,
  scaleItemsCount,
  scaleItemsCountFormatted,
  scaleItemWeightCalibrated,
  scaleItemsCountTaken,
  scaleItemsCountTakenFormatted,
  isScaleValueStable,
  scaleStableValue,
  initialScaleItemsCount,
  minInitialLoad,
  isScaleValueApplied,
  isScaleValueRequired,
  isScaleAllowContinue,
  tare,
  scaleItemUpdate,
  applyEmptyInsertWeight,
  applyScaleItemWeight,
  applyScaleItemsCount,
  applyScaleCalibration,
  cancelScaleCalibration,
  initializeWeightScale,
  disposeWeightScale,
  setWeightScaleCalibrationMode,
} = articleWeight;

const {
  isReferencing,
  isReferenced,
  isOpening,
  isOpen,
  isPrevOpen,
  isReopen,
  isClosing,
  isClosed,
  isAnyOpening,
  isAnyOpen,
  isAnyDoorOpen,
  cabinetUnits,
  cabinetList,
  areLockerCabinets,
  allCabinetIds,
  cabinetId,
  cabinetIds,
  isMultiCabinet,
  primaryCabinetId,
  lastRequest,
  lastOpenTrayArgs,
  lastOpenTrayController,
  lastOpenTrayAction,
  hasDeliveryFeature,
  initializeCabinetControllers,
  disposeCabinetControllers,
  updateMachineReferenceStatus,
  openTray,
  reopenTray,
  closeTray,
} = cabinetState;

const {
  loadCabinets,
  loadParameters,
  loadCategories,
  preloadArticlesOnce,
  loadArticles,
  loadUser,
  loadUsers,
  loadUserInventory,
  loadCabinetStock,
} = catalogLoader;

const {
  isLoading,
  user,
  users,
  isAdmin,
  currentUserId,
  canCalibrateWeightScale,
  error,
  reportEmptyStock,
  bestTrayPreference,
  isDevelopmentMode,
  isLocalCacheEnabled,
  autoConfirm,
  autoReopenDelay,
  isFilterVisible,
  isLargeImageView,
  canAutoCloseModal,
  allowPrivateCloseChoice,
  isPrivateCloseChoice,
  isShowAllArticles,
  isShowAllArticlesInOrganization,
  isUserPrefSize,
  isNotRef,
  selectedSortOption,
  displayMode,
  rows,
  rowsLoaded,
  rowsFiltered,
  rowsBorrowed,
  rowsAvailable,
  singleArticle,
  suggestions,
  allPossibleCategories,
  allPossibleParameters,
  allPossibleArticles,
  allPossibleArticles2,
  selected,
  isModalVisible,
  selectedTrayName,
  selectedTrayItem,
  selectedTrayUsage,
  selectedUsage,
  availableTraySelect,
  articlesList,
  cabinetFeatures,
  cabinetStock,
  cabinetTrays,
  privateTrays,
  privateTraysFiltered,
  hasPrivateTrays,
  parentCategoryId,
  selectedCategoryId,
  currentCategory,
  allowImageFallback,
  currentCategoryImageUrl,
  nearestParentCategorySmallImageUrl,
  nearestParentCategoryLargeImageUrl,
  categoryList,
  categorySubtree,
  categoryTree,
  categories,
  categoriesFiltered,
  categoryDesign,
  categoryBreadcrumbs,
  isRootCategory,
  isLastCategory,
  isCategorySectionVisible,
  parameters,
  hasParameterSelected,
  isParameterSectionVisible,
  isArticleSectionVisible,
  isMapView,
  isLoadingLocation,
  isLocationView,
  hasLocation,
  cabinetLocation,
  relatedStock,
  relatedStockIndex,
  relatedStockCount,
  relatedStockCurrent,
  isRelatedArticlesView,
  hasRelatedArticles,
  canOpenTrayStatus,
  canOpenTrayPermissions,
  isTakenByOtherUser,
  canOpenTray,
  canReturnArticle,
  isSingleArticle,
  isPrivateVisible,
  isCabinetPrivateMode,
  isCabinetBorrowModeDefault,
  isCabinetBorrowMode,
  availableTrayOptions,

  //Functions
  showModal,
  closeModal,
  closeModalConditional,
  selectArticle,
  selectCategory,
  selectParameter,
  isVaultItem,
  showLocation,
  hideLocation,
  showCabinetMap,
  showCabinetPicture,
  nextRelatedStock,
  prevRelatedStock,
  showRelatedArticles,
  hideRelatedArticles,
  getUserDisplayName,
  resolveImageUrl,
  setDisplayMode,
  sortArticles,
  applyTrayUsage,
} = catalogState;

const {
  userItems,
  isAutoUserItems,
  isUserItemsVisible,
  isLoadingUserItems,
  isPrivateView,
  isPrivateModalVisible,
  autoSelectPrivateTray,
  userPrivateTray,
  userPrivateTrayName,
  availablePrivateTraysCount,
  isReturningPrivateTray,
  isOpenPrivateTray,
  canOpenPrivateTray,
  showUserItems,
} = userInventory;

/******************************************************************************************
 * UI component mounted/unmounted
 ******************************************************************************************/
onMounted(async () => {
  useTitle(t('menu.catalog'));
  useMenu("catalog");
  useSubmenu(false);
  useTopBar(true);

  //Design time
  isLoading.value = false;

  //useSessionTimer(true);

  initializeComponents();
  // initializeWeightScale();
});

onUnmounted(async () => {  
  // disposeCabinetControllers();
  // disposeWeightScale();
});

onCatalogLoad((item) => {

});

onArticleSelect((item) => {

});

onCategorySelect((categoryId) => {
});

onParameterSelect((options) => {

});

onSearchResultSelect((item) => {

});

onAlarmContinue(() => {
  continueAfterAlarm();
});

/******************************************************************************************
 * Initialization
 ******************************************************************************************/
async function initializeComponents() {
  try {
    let sw = new Stopwatch();
    isLoading.value = true;

    console.log("[benchmark] Check point 1:", catalogStopwatch.elapsed() + "ms (initializeComponents)", catalogStopwatch.delta());
    
    await initializeUser();
    console.log("[benchmark] Check point 3:", catalogStopwatch.elapsed() + "ms (initializeUser)", catalogStopwatch.delta());

    //Load user defined settings for this cabinet
    await initializeSettings();
    console.log("[benchmark] Check point 4:", catalogStopwatch.elapsed() + "ms (initializeSettings)", catalogStopwatch.delta());

    //Initialize hardware communication
    await initializeCabinetControllers();
    console.log("[benchmark] Check point 5:", catalogStopwatch.elapsed() + "ms (initializeCabinetControllers)", catalogStopwatch.delta());

    refreshCabinetControllersStatus();

    await loadCabinets();
    console.log("[benchmark] Check point 8:", catalogStopwatch.elapsed() + "ms (loadCabinets)", catalogStopwatch.delta());

    const isPromiseAll = ref(false);
    if (!isPromiseAll.value) {
      //Sequential loading
      await loadParameters();
      console.log("[benchmark] Check point 9a:", catalogStopwatch.elapsed() + "ms (loadParameters)", catalogStopwatch.delta());

      await loadCabinetStock(); //Takes 2s!
      console.log("[benchmark] Check point 9b:", catalogStopwatch.elapsed() + "ms (loadCabinetStock)", catalogStopwatch.delta());

      await loadArticles();
      console.log("[benchmark] Check point 9c:", catalogStopwatch.elapsed() + "ms (loadArticles)", catalogStopwatch.delta());

      await loadUserInventory();
      console.log("[benchmark] Check point 9d:", catalogStopwatch.elapsed() + "ms (loadUserInventory)", catalogStopwatch.delta());
    }
    else {
      //Parallel loading can improve loading performance for 0-500 ms
      await Promise.all([ loadParameters(), loadCabinetStock(), loadArticles(), loadUserInventory() ]);
      console.log("[benchmark] Check point 9:", catalogStopwatch.elapsed() + "ms (articles, stock, parameters, user inventory)", catalogStopwatch.delta());
    }

    await loadCategories();
    selectCategory("_root");
    console.log("[benchmark] Check point 10:", catalogStopwatch.elapsed() + "ms (loadCategories)", catalogStopwatch.delta());

    await loadUser();
    isUserPrefSize.value = true;
    console.log("[benchmark] Check point 11:", catalogStopwatch.elapsed() + "ms (loadUser)", catalogStopwatch.delta());
    
    await loadUsers(); //Can be postponed

    console.log("[benchmark] Check point 12:", catalogStopwatch.elapsed() + "ms (loadUsers)", catalogStopwatch.delta());

    //Immeditely show list of current user's borrowed tools (if settings are set so)
    if (settings.value?.preferences?.listUsersItems && !showUserItemsDisplayedOnce.value) {
      showUserItems({ mustHaveItems: true, isAutoLoad: true });
      console.log("[benchmark] Check point 13:", catalogStopwatch.elapsed() + "ms (showUserItems)", catalogStopwatch.delta());
      showUserItemsDisplayedOnce.value = true;
      setTimeout(() => {
        isLoading.value = false;
      }, 500);
    }
    else {
      isLoading.value = false;
    }

    console.log("[benchmark] Total load time:", catalogStopwatch.elapsed() + "ms");

    // catch (err) {
    //   console.error("[benchmark] Loading finished with error:", catalogStopwatch.elapsed() + "ms", err);
    //   if (err.name == "SyntaxError" || err.message.startsWith("JSON.parse")) {
    //     //toast(new Error(t('catalog.component_not_responding')));
    //     error.value = new Error(t('catalog.component_not_responding'));
    //   }
    //   else if (err.message.includes("Cabinet not found")) { //TODO: Error code
    //     toast(new Error(t('catalog.cabinet_not_configured')));
    //   }
    //   else {
    //     toast(err);
    //   }
    // }
  }
  catch (err) {
    console.error("[catalog] Initialization error", err)
    err = translateError(err);
    if (err.isSyntaxError) {
      error.value = new Error(t('catalog.component_not_responding'));
    }
    else {
      error.value = err;
    }
  }
}

/******************************************************************************************
 * Load user and apply permissions
 ******************************************************************************************/
async function initializeUser() {
  //Check if cabinet admin
  const permissions = authStore.context.permissions;
  isAdmin.value = permissions.includes("cabinet.admin");
  canCalibrateWeightScale.value = permissions.includes("cabinet.admin") || permissions.includes("cabinet.service");
  canTransferStock.value = permissions.includes("cabinet.admin") || permissions.includes("cabinet.service");
  currentUserId.value = authStore.context?.user?.id;
}

/******************************************************************************************
 * Load and apply settings
 ******************************************************************************************/
async function initializeSettings() {
  //Local cache of articles and images for quick loading and offline environment
  if (settings.value?.preferences?.localCache) {
    isLocalCacheEnabled.value = settings.value?.preferences?.localCache == "enabled";
  }

  //Automatic tray selection mode algorithm to be used
  if (settings.value.usage?.weightMeasure) {
    isScaleValueRequired.value = settings.value.usage?.weightMeasure == "required"; //required, optional
  }

  //Automatic tray selection mode algorithm to be used
  if (settings.value.catalog?.traySelectMode) {
    bestTrayPreference.value = settings.value.catalog?.traySelectMode;
  }

  //Send email on empty stock
  if (settings.value.catalog?.canSendEmptyStockEmail) {
    reportEmptyStock.value = true;
  }

  //Delay reopening cabinet tray if user didn't fulfill the requirements
  let delay = settings.value.preferences?.autoReopenDelay;
  autoReopenDelay.value = parseInt(delay) > 0 ? parseInt(delay) : null;

  //Display mode: list or grid
  if (settings.value?.catalog?.displayMode) {
    setDisplayMode(settings.value?.catalog?.displayMode);
  }

  //Debug flag
  isDevelopmentMode.value = settings.value?.debug?.enabled;
}

/******************************************************************************************
 * Weight scale
 ******************************************************************************************/
function refreshCabinetControllersStatus() {
  //Get cabinet reference position status
  setTimeout(async () => {
    try {
      await updateMachineReferenceStatus();
    }
    catch (err) {
      err = translateError(err);
      if (err.isSyntaxError) {
        toast(new Error(t('catalog.hcu_not_responding')));
      }
      else {
        toast(err);
      }
    }
  }, 200);
}

/******************************************************************************************
 * Weight scale
 ******************************************************************************************/




function continueAfterAlarm() {
  console.log("continueAfterAlarm", isHaltAlarm.value, lastOpenTrayArgs.value)

  //Only if emergency stop button pressed
  if (isHaltAlarm.value) {
    console.log("continueAfterAlarm", "resent last command", "canOpenTray", canOpenTray.value, "=>", true);
    let controller = lastOpenTrayController.value;
    if (controller) {
      //isOpen.value = false;
      //isClosing.value = false;
      isPrevOpen.value = false;
      //isOpening.value = true;
      canOpenTray.value = true;
      isEditStockQuantity.value = false; //TODO: Show close window
      //isEditStockQuantity.value = true; //TODO: temporary so user can close the window
      if (lastOpenTrayArgs.value && lastOpenTrayAction.value == "open") {
        //Resend last command
        let args = { ...lastOpenTrayArgs.value };
        args.skipParameters = true;
        console.log("continueAfterAlarm", "openTray", args);
        controller.openTray(args);
      }
      else if (lastRequest.value && lastRequest.value.tray) { //if (lastOpenTrayAction.value == "close")
        //Resend last command
        let args = lastRequest.value.tray;
        console.log("continueAfterAlarm", "closeTray", args);
        controller.closeTray(args);
      }
      else {
        console.log("continueAfterAlarm", "error: unknown", lastOpenTrayArgs.value, lastRequest.value);
      }
    }
    else {
      console.log("continueAfterAlarm", "error: no controller");
    }
  }

  console.log("continueAfterAlarm", "clear alarm");
  clearAlarm();
}



async function onSearchRequest(value) {
  try {
    loadArticles();
  }
  catch (err) {
    //dialog(err.message)
  }
}

function onSearchTextChanged(value) {
  //Uncomment for instant search; comment to wait for "Enter" key press
  onSearchRequest(value);
}

/******************************************************************************************
 * Event when tray is selected in drop down
 ******************************************************************************************/
function onTraySelect() {
  let option = null;
  if (availableTraySelect.value && availableTraySelect.value != "none") {
    option = availableTrayOptions.value?.find(x => x.id == availableTraySelect.value);
  }

  let tray = option?.tray;

  selectedTrayItem.value = tray;
  selectedTrayName.value = tray ? tray.name : null;
  selectedUsage.value = tray ? tray.usage : null;
  stockComment.value = tray ? tray.comment : null; //TODO: Comment is not based only on tray, but specifically on article of tray

  //Fix to keep displaying total stock on drop down option change
  isCabinetBorrowModeDefault.value = getCabinetMode(tray?.cabinetId) == "vault";
}

function setArticleUsage(usage) {
  // if (selectedTrayItem.value) {
  //   selectedTrayItem.value.usage = usage;
  // }
  selectedUsage.value = usage;
}

// function setTrayUsage(usage) {
//   selectedTrayUsage.value = usage;
// }

function startReturnArticleProcedure() {
  returnArticle.value = itemBorrowed.value;
  returnArticleTrayName.value = itemBorrowed.value?.tray?.name;
  isReturnProcedure.value = true;
  isTakingProcedure.value = false;
  openTray(returnArticleTrayName.value);
}

function takeNextArticle() {
  takeArticle.value = itemAvailable;
  takeArticleTrayName.value = itemAvailable.value?.tray?.name;
  isReturnProcedure.value = false;
  isTakingProcedure.value = true;
  openTray(takeArticleTrayName.value);
}

async function cancelReturnArticle() {
  isReturnProcedure.value = isOpen.value;
  isChoosingAction.value = false;
  //returnArticle.value = null;
  //takeArticle.value = null;
}

async function confirmReturnArticle() {
  isReturnProcedure.value = isOpen.value;
  isChoosingAction.value = false;
  //returnArticle.value = null;
  //takeArticle.value = null;
  //TODO: Save to database

  if (!itemBorrowed.value?.id) {
    return dialog(new Error("itemBorrowed not found!"))
  }

  let article = rows.value.find(x => x.id == itemBorrowed.value.id);
  if (!article) {
    return dialog(new Error("Article not found!"))
  }

  let tray = article.trays.find(x => x.name == returnArticleTrayName.value);
  if (!tray) {
    return dialog(new Error("Tray not found!"))
  }

  tray.userId = user.value?.id;
  tray.usage = "used";
  //itemBorrowed.value == null

  try {
    let data = {
      trays: [
        {
          findTrayId: true,
          articleId: article.id,
          quantity: 0, //Avoid counting in sum
          unit: "pcs",
          itemWeight: article.itemWeight,
          name: tray.name, //Tray name, e.g. A1
          usage: tray.usage,
          userId: tray.userId,
          userTime: utc(),
        }
      ]
    };

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data, { reportEmptyStock: reportEmptyStock.value });
  }
  catch (err) {
    return dialog(err);
  }

  try {
    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.returnItem",
        t: "cabinet",
        s: "info",
        uid: currentUserId.value,
        //un: user.value?.displayName || user.value?.username,
        cid: tray.cabinetId,
        tid: tray.trayId || tray.id,
        tn: tray.name,
        msg: `Vračilo uporabljenega artikla "${article.name}" v predal "${tray.name}".`,
        ts: new Date()
      });
    }
  }
  catch (err) {
    console.error(err);
  }
}


async function cancelTakeArticle() {
  isTakingProcedure.value = isOpen.value;
  isChoosingAction.value = false;
}

async function confirmTakeArticle() {
  isTakingProcedure.value = isOpen.value;
  isChoosingAction.value = false;

  //TODO: Save to database

  if (!itemAvailable.value?.id) {
    return dialog(new Error("itemAvailable not found!"))
  }

  let article = rows.value.find(x => x.id == itemAvailable.value.id);
  if (!article) {
    return dialog(new Error("Article not found!"))
  }

  let tray = article.trays.find(x => x.name == takeArticleTrayName.value);
  if (!tray) {
    return dialog(new Error("Tray not found!"))
  }

  tray.userId = user.value?.id;
  tray.usage = "borrowed";

  try {
    let data = {
      trays: [
        {
          findTrayId: true,
          articleId: article.id,
          quantity: 0, //Avoid counting in sum
          unit: "pcs",
          itemWeight: article.itemWeight,
          name: tray.name, //Tray name, e.g. A1
          usage: tray.usage,
          userId: tray.userId,
          userTime: utc(),
        }
      ]
    };

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data, { reportEmptyStock: reportEmptyStock.value });
  }
  catch (err) {
    return dialog(err);
  }

  try {
    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.takeItem",
        t: "cabinet",
        s: "info",
        //uid: user.value?.displayName || user.value?.username,
        uid: currentUserId.value,
        cid: tray.cabinetId,
        tid: tray.trayId || tray.id,
        tn: tray.name,
        msg: `Izposoja artikla "${article.name}" is predala "${tray.name}".`,
        ts: new Date()
      });
    }
  }
  catch (err) {
    console.error(err);
  }
}

function enableModalCloseButton(visible) {
  let button = document.querySelector(".non-escapable-modal button.text-gray-400.bg-transparent");
  if (button) {
    button.style.display = visible ? "block" : "none";
  }
}

async function logout(e) {
  try {
    //Hide menu
    useMenu(false);
    useSubmenu(false);
    useTopBar(false);
    
    //Redirect to login page
    $router.push({ name: "Login", query: { closeTrayAutoLogout: true } });
  }
  catch (err) {
    console.error(err);
  }
}

watch(isOpening, async (value, previous) => {
  console.log("[watch] isOpening", previous, "=>", value);
  enableModalCloseButton(!value);
});

watch(isClosing, async (value, previous) => {
  console.log("[watch] isClosing", previous, "=>", value);
  enableModalCloseButton(!value);
});

watch(isOpen, async (value, previous) => {
  console.log("[watch] isOpen", value);
  enableModalCloseButton(!value);

  //Clear banner
  hideWarningBanner();
  clearTimeout(openTimer);

  //TODO: also clear banner on user activity, e.g. touch
  //clearTimeout(openTimer);

  //TODO: Local events like "tray open", "tray closed", "tray opening", "tray closing"

  //Detected change from open to closed
  if (previous && !value) {
    isClosing.value = false;

    //Automatically hide modal window when tray is closed
    if (canAutoCloseModal.value) {
      closeModal();
    }
  }

  if (isOpenPrivateTray.value) {
    /*
    try {
      console.log("user private", userPrivateTray.value, currentUserId.value)
      //Save changes to database
      await saveChanges(userPrivateTray.value.cabinetId, {
        trays: [
          {
            trayId: userPrivateTray.value.trayId,
            name: userPrivateTray.value.name,
            status: isReturningPrivateTray.value ? "active" : "occupied",
            //userId: isReturningPrivateTray.value ? null : currentUserId.value,
            privateUserId: isReturningPrivateTray.value ? null : currentUserId.value,
            occupiedTime: isReturningPrivateTray.value ? null : utc(),
          }
        ]
      });
    }
    catch (err) {
      console.error(err);
      dialog(err);
    }

    if (isReturningPrivateTray.value) {
      userPrivateTray.value = null;
    }*/
  }

  //Only for borrw vault and private vault
  if (isCabinetBorrowMode.value || isCabinetPrivateMode.value) {

    //Change to "open"
    if (value) { //TODO: settings.value alarm

      //Only if defined in settings
      let alertTimeout = settings.value?.session?.alertTimeout;
      if (alertTimeout > 0) {
        //Show "tray open" warning after few seconds
        let ms = parseInt(alertTimeout) * 1000;
        openTimer = setTimeout(() => {
          showWarningBanner(isCabinetPrivateMode.value ? "privateWindow" : "articleWindow");
        }, ms);
      }

      //Change to active or occupied for private tray
      if (isCabinetPrivateMode.value) {
        if (allowPrivateCloseChoice.value) {
          //When user needs to choose something on the screen to complete the action
          isPrivateCloseChoice.value = true;
        }
        else {
          //Automatically toggle status upon opening cabinet tray - no user action on screen is needed
          try {
            let tray = selectedTrayItem.value;
            let newStatus = tray.status != "occupied" ? "occupied" : "active";
            let result = await cabinetService.updateCabinetStock(tray.cabinetId, {
              trays: [
                {
                  trayId: tray.trayId,
                  name: tray.name,
                  status: newStatus,
                  privateUserId: newStatus == "occupied" ? currentUserId.value : null,
                  occupiedTime: utc()
                }
              ]
            }, { reportEmptyStock: reportEmptyStock.value });
            selectedTrayItem.value.status = newStatus;
          }
          catch (err) {
            console.error("updateCabinetStock", err);
          }
        }
      }

    }

    //Change to "close"
    if (!value && settings.value?.session?.trayAutoLogout == true) {
      //Auto log out after closing the tray
      logout();
    }

  }

  //Borrow mode
  if (isReturnProcedure.value) {
    //Tray just opened
    if (value && !previous) {
      isChoosingAction.value = true;
    }

    //Tray just closed
    if (!value && previous) {
      isReturnProcedure.value = isChoosingAction.value;
    }
  }

  //Borrow mode
  if (isTakingProcedure.value) {
    //Tray just opened
    if (value && !previous) {
      isChoosingAction.value = true;
    }

    //Tray just closed
    if (!value && previous) {
      isTakingProcedure.value = isChoosingAction.value;
    }
  }

  if (isModalVisible.value && !isEditStockQuantity.value && previous == true && value == false) {
    closeModal(); //Automatically close modal window when door is closed
  }

  /*
  if (!value && previous) {
    try {
      if (appState.isLogEnabled) {
        let result = await logService.insertLog({
          a: "cabinet.tray",
          t: "cabinet",
          s: "info",
          uid: user.value?.displayName || user.value?.username,
          tid: tray.name,
          msg: `Predal "${tray.name}" je zaprt.`,
          ts: new Date()
        });
      }
    }
    catch (err) {
      console.error(err);
    }
  }
  */
});

watch(isEditStockQuantity, (value) => {
  console.log("[watch] isEditStockQuantity", value);
  if (value && autoConfirm.value) {
    //Auto-click button
    if (isCabinetBorrowMode.value && canSaveStockQuantity.value) {
      if (selectedTrayUsage.value != 'taken') {
        applyTrayUsage('taken');
        applyStock(selected.value);
      }
      else if (selectedTrayUsage.value != 'present') {
        applyTrayUsage('present');
        applyStock(selected.value);
      }
    }
  }
});

watch(selectedSortOption, (value, previous) => {
  sortArticles();
});

const userGloveSize = computed(() => {
  return user.value?.preferences?.gloveSize; //S, M, L
});

watch(isUserPrefSize, (value, previous) => {
  let size = allPossibleParameters.value?.find(x => x.name == "Velikost");

  if (size) {
    //Selected fixed size, cannot be changed
    size.locked = isUserPrefSize.value;
  }

  if (size && size.options && size.options.length && userGloveSize.value) {
    let option = size.options.find(x => x.name == userGloveSize.value);
    if (option) {
      option.selected = (value == true);

      //This will "loadArticles" on every change
      //selectParameter(option);

      //Better to first mark all selections, then call "loadArticles" once
      let otherOptions = size.options.filter(x => x.id != option.id);
      for (let other of otherOptions) {
        other.selected = false;
      }
      
      //Apply filter
      try {
        loadArticles();
      }
      catch (err) {
        //dialog(err.message)
      }
    }
  }
});

function onSelectedTrayItem(tray) {
  let hasAccess = false;

  //Defined permissions
  if (tray) {
    let userId = currentUserId.value;

    //No restrictions if no permissions set
    if (!tray.permissions || tray.permissions.length == 0) {
      hasAccess = true;
    }
    else {
      //Check permissions for this specific user
      hasAccess = userId && tray.permissions.find(x => x.userId && x.userId == userId) ? true : false;

      //Check permissions for the group
      if (!hasAccess && tray.permissions.filter(x => x.userGroupId).length > 0) {
        //TODO: Check also the group
        //TODO: Must the group where user is in
        hasAccess = true;
      }
    }

    //Check if taken by another user - only the same user (or admin) can return item
    if (tray.usage == "taken" && tray.userId != userId) {
      hasAccess = false;
      isTakenByOtherUser.value = true;
    }
    else {
      isTakenByOtherUser.value = false;
    }

    //Admin has full permissions
    if (isAdmin.value) {
      hasAccess = true;
    }

    //Take or return option
    selectedTrayUsage.value = tray.usage; //Preselected
    //selectedTrayUsage.value = null; //Force user to choose before confirm

    //Clear editable comment
    stockComment.value = tray.comment || "";
  }

  canOpenTrayStatus.value = (tray && tray.status != "locked");
  canOpenTrayPermissions.value = hasAccess;
}

watch(selectedTrayItem, onSelectedTrayItem);

//or if used clicks on a button up/down for quantity
watch(virtualStockQuantity, () => {
  isScaleValueApplied.value = true;
  clearTimeout(reopenTimer);
});

async function saveChanges(cabinetId, changes) {
  try {
    let result = await cabinetService.updateCabinetStock(cabinetId, changes, { reportEmptyStock: reportEmptyStock.value });
  }
  catch (err) {
    console.error("saveChanges", err);
    dialog(err);
    throw err;
  }
}

function referencingDone() {
  isNotRef.value = false;
}

function noImage(selected) {
  if (!selected) {
    return;
  }
  selected.originalImageUrl = null;
  selected.largeImageUrl = null;
  selected.mediumImageUrl = null;
  selected.smallImageUrl = null;
  selected.thumbnailUrl = null;
  /*
  originalImageId: x.originalImageId,
  largeImagePath: x.largeImagePath,
  largeImageUrl: resolveImageUrl(x.largeImagePath),
  mediumImagePath: x.mediumImagePath,
  mediumImageUrl: resolveImageUrl(x.mediumImagePath),
  smallImagePath: x.smallImagePath,
  smallImageUrl: resolveImageUrl(x.smallImagePath),
  thumbnailPath: x.thumbnailPath,
  thumbnailUrl: resolveImageUrl(x.thumbnailPath),
  */
}
</script>

<template>
  <div class="screen-container">

    <!-- Error message -->
    <ErrorMessage :error="error" v-if="error" />

    <!-- Loading animation -->
    <div class="grid place-items-center w-full h-[calc(100vh-120px)] " v-else-if="isLoading && !isNotRef">
      <Spinner size="12"></Spinner>
    </div>

    <!-- <ReferencePosition @done="referencingDone" /> -->

    <!-- Header -->
    <div class="w-full mx-auto flex items-center justify-between" v-if="!isLoading && !isNotRef && (!isCabinetBorrowMode || !isSingleArticle) && !isPrivateVisible && !isUserItemsVisible&& !error">

      <div class="text-lg font-bold dark:text-white">{{ $t('menu.catalog') }}</div>

      <div class="flex flex-row justify-items-strech space-x-2">

        <!-- Search box -->
        <AutocompleteTextBox v-model="searchText" :placeholder="$t('catalog.search_placeholder')" :loading="false" :auto-search="true" :resizable="false" :suggestions="suggestions" @text-changed="onSearchTextChanged" @search-request="onSearchRequest"></AutocompleteTextBox>

        <!-- Display mode -->
        <Button type="secondary" v-if="displayMode == 'list'" @click="setDisplayMode('grid')" class="w-14"><i class="fa fa-list"></i></Button>
        <Button type="secondary" v-if="displayMode == 'grid'" @click="setDisplayMode('list')" class="w-14"><i class="fa fa-grip"></i></Button>

        <!-- Filter icon -->
        <Button type="secondary" v-if="!isFilterVisible" @click="() => isFilterVisible = true" class="w-14"><i class="fa fa-filter"></i></Button>
        <Button type="primary" v-if="isFilterVisible" @click="() => isFilterVisible = false" class="w-14"><i class="fa fa-filter"></i></Button>

        <!-- User items -->
        <Button type="secondary" v-if="!isUserItemsVisible" @click="showUserItems()" class="w-14"><i class="fa fa-user"></i></Button>

      </div>
    </div>

    <!-- Content -->
    <div v-if="!isLoading && !isNotRef && !error">

    <!-- <pre class="dark:bg-amber-900 border rounded-md mt-5 p-5">isLoading: {{ isLoading }}
isAdmin: {{ isAdmin }}
isAnyAlarm: {{ isAnyAlarm }}
isShowAllArticles: {{ isShowAllArticles }}
isWarningBannerVisible: {{ isShowAllArticles }}
selectedSortOption: {{ selectedSortOption }}</pre> -->

      <!-- Category buttons -->
      <CategorySelect v-if="isCategorySectionVisible && !isSearchFilter && !isPrivateVisible && !isUserItemsVisible" />

      <!-- Parameter buttons -->
      <ParameterSelect v-if="isParameterSectionVisible" />

      <!-- Article buttons -->
      <ArticleSelect v-if="!isSearchFilter && isArticleSectionVisible && !isPrivateVisible && !isUserItemsVisible" />

      <!-- Open private tray -->
      <PrivateTraySelect @escape="selectCategory('special:root')" v-else-if="!isSearchFilter && isPrivateVisible" />

      <!-- User's inventory: private trays, borrowed items, pending transfers -->
      <UserInventory @escape="selectCategory('special:root')" v-else-if="!isSearchFilter && isUserItemsVisible" />

      <!-- Search results -->
      <SearchResults v-if="isSearchFilter" />

      <!-- Distancer -->
      <div class="distancer" style="height: 10px;">
      </div>

    </div>

    <!-- Article details in modal window -->
    <ArticleModal @click="(e) => closeModalConditional(e)" v-if="isModalVisible && selected && !isAnyAlarm" />

    <!-- Private tray info -->
    <PrivateTrayModal @close="(e) => closePrivateModalConditional(e)" v-if="isPrivateModalVisible && !isAnyAlarm" />

    <!-- Barcode scanned: article selection -->
    <BarcodeModal @close="(e) => closeBarcodeModal(e)" v-if="isBarcodeModalVisible && !isAnyAlarm" />

    <!-- Alarm signal from the hardware -->
    <AlarmModal :canClear="isAdmin" :canContinue="true" @continue="() => continueAfterAlarm(selectedTrayItem)" v-if="isAnyAlarm" />
    
    <!-- Alert banner on the top if any tray is left open for too long -->
    <AlertBanner v-if="isWarningBannerVisible" />
    
    <!-- Filter options in the right-side drawer -->
    <FilterOptionsDrawer />

    <!-- Distancer for virtual keyboard so the content doesnt get overlapped -->
    <div class="distancer keyboard-height" v-if="isKeyboardVisible"></div>

  </div>
</template>

<style scoped>
.screen-container {
  @apply grid relative dark:bg-gray-900 dark:text-white p-5;
}
</style>
